import { useState, useRef, useEffect, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function SVGMapViewer({ svgContent, className, showControls = true }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Refs mirror mutable drag state so event-handler callbacks have zero deps
  // and are never re-created (avoids re-attaching listeners on every frame).
  const positionRef   = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef  = useRef({ x: 0, y: 0 });
  // Pinch state is ref-only (never needs to trigger a render)
  const pinchRef      = useRef({ active: false, lastDist: 0 });

  // Helper: update both the React state (triggers render) and the ref (instant).
  const applyPosition = useCallback((pos) => {
    positionRef.current = pos;
    setPosition(pos);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleReset = useCallback(() => {
    setScale(1);
    applyPosition({ x: 0, y: 0 });
  }, [applyPosition]);

  // --- Mouse handlers ---
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      isDraggingRef.current = true;
      setIsDragging(true);
      dragStartRef.current = {
        x: e.clientX - positionRef.current.x,
        y: e.clientY - positionRef.current.y,
      };
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDraggingRef.current) {
      applyPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y,
      });
    }
  }, [applyPosition]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((prev) => Math.min(Math.max(prev + delta, 0.5), 3));
  }, []);

  // --- Touch handlers (pan + pinch-zoom) ---
  // All touch callbacks have empty dependency arrays because they only access
  // refs, eliminating constant listener re-attachment during drag/pinch.
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    const touches = e.touches;
    if (touches.length === 1) {
      isDraggingRef.current = true;
      setIsDragging(true);
      dragStartRef.current = {
        x: touches[0].clientX - positionRef.current.x,
        y: touches[0].clientY - positionRef.current.y,
      };
      pinchRef.current.active = false;
    } else if (touches.length === 2) {
      isDraggingRef.current = false;
      setIsDragging(false);
      pinchRef.current.active = true;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      pinchRef.current.lastDist = Math.sqrt(dx * dx + dy * dy);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    const touches = e.touches;
    if (touches.length === 1 && isDraggingRef.current && !pinchRef.current.active) {
      applyPosition({
        x: touches[0].clientX - dragStartRef.current.x,
        y: touches[0].clientY - dragStartRef.current.y,
      });
    } else if (touches.length === 2 && pinchRef.current.active) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (pinchRef.current.lastDist > 0) {
        const ratio = dist / pinchRef.current.lastDist;
        setScale((prev) => Math.min(Math.max(prev * ratio, 0.5), 3));
      }
      pinchRef.current.lastDist = dist;
    }
  }, [applyPosition]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      isDraggingRef.current = false;
      setIsDragging(false);
      pinchRef.current.active = false;
    } else if (e.touches.length === 1) {
      // Transition from pinch back to single-finger pan
      pinchRef.current.active = false;
      isDraggingRef.current = true;
      setIsDragging(true);
      dragStartRef.current = {
        x: e.touches[0].clientX - positionRef.current.x,
        y: e.touches[0].clientY - positionRef.current.y,
      };
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      container.addEventListener("touchstart", handleTouchStart, { passive: false });
      container.addEventListener("touchmove", handleTouchMove, { passive: false });
      container.addEventListener("touchend", handleTouchEnd);
      return () => {
        container.removeEventListener("wheel", handleWheel);
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  if (!svgContent) {
    return (
      <div className={cn(
        "relative w-full h-[60vh] md:h-[70vh] bg-[#0B1220] border border-white/10 flex items-center justify-center",
        className
      )}>
        <div className="text-center">
          <Move className="w-12 h-12 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#6B7280] text-sm uppercase tracking-wider">No map available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} data-testid="svg-map-viewer">
      {/* Map Container */}
      <div
        ref={containerRef}
        className={cn(
          "relative w-full h-[60vh] md:h-[70vh] overflow-hidden bg-[#0B1220] border border-white/10",
          isDragging ? "cursor-grabbing" : "cursor-grab"
        )}
        style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Viewfinder Corners */}
        <div className="viewfinder-corner top-left" />
        <div className="viewfinder-corner top-right" />
        <div className="viewfinder-corner bottom-left" />
        <div className="viewfinder-corner bottom-right" />

        {/* SVG Content */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.1s ease-out",
            willChange: "transform",
          }}
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />

        {/* Scale Indicator */}
        <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 bg-[#0B1220]/90 border border-white/10">
          <span className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider">
            Zoom: {Math.round(scale * 100)}%
          </span>
        </div>
      </div>

      {/* Controls */}
      {showControls && (
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomIn}
            className="w-9 h-9 bg-[#0B1220]/90 border-white/10 hover:bg-[#1e3a5f] hover:border-[#FBBF24]/30 rounded-none"
            data-testid="zoom-in-btn"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleZoomOut}
            className="w-9 h-9 bg-[#0B1220]/90 border-white/10 hover:bg-[#1e3a5f] hover:border-[#FBBF24]/30 rounded-none"
            data-testid="zoom-out-btn"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleReset}
            className="w-9 h-9 bg-[#0B1220]/90 border-white/10 hover:bg-[#1e3a5f] hover:border-[#FBBF24]/30 rounded-none"
            data-testid="reset-zoom-btn"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// Thumbnail version for archive
// Uses an <img> with a data URL so each SVG is sandboxed, preventing
// shared `id` attribute collisions (gradients, clipPaths, etc.) that
// cause all thumbnails to render as a copy of the last SVG on the page.
export function SVGMapThumbnail({ svgContent, className }) {
  if (!svgContent) {
    return (
      <div className={cn(
        "w-full h-32 bg-[#0B1220] border border-white/10 flex items-center justify-center",
        className
      )}>
        <span className="text-[#6B7280] text-xs uppercase">No Preview</span>
      </div>
    );
  }

  const dataUrl = `data:image/svg+xml,${encodeURIComponent(svgContent)}`;

  return (
    <div
      className={cn(
        "w-full h-32 overflow-hidden bg-[#0B1220] border border-white/10 relative",
        className
      )}
      data-testid="svg-map-thumbnail"
    >
      <img
        src={dataUrl}
        alt="Outlook map preview"
        className="w-full h-full object-contain"
      />
    </div>
  );
}
